import ExpandMore from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { encampBrandColors } from "colors";
import {
  NotificationPreferenceScope,
  NotificationPreferenceSource,
} from "generated-graphql/graphql";
import { useId } from "react";

import {
  AlertRow,
  PREFERENCE_GRID_COLUMNS,
  PREFERENCE_GRID_MIN_WIDTH,
  type AlertRowData,
} from "./AlertRow";
import { CHANNEL_CELL_SX, channelLabel, SECTION_OFF_LABEL } from "./preferenceProvenance";
import { SectionRollupRow } from "./SectionRollupRow";
import {
  countDeliveringRows,
  type DeliverySection,
  type EmailCadenceChoice,
  PREFERENCE_ROW_CHANNELS,
  type DraftParentSwitch,
  type NotificationPreferenceDraft,
} from "./useNotificationPreferenceDraft";

/**
 * One section of the preferences page: a heading, a {@link SectionRollupRow} for the
 * whole category, and the notification rows beneath it.
 *
 * The heading says "Off" when nothing in the section will fire and says nothing
 * otherwise. It can disagree with the switches inside, because a page-wide channel
 * mute silences them without moving them.
 */

export type AlertSectionData = {
  groupId: string;
  label: string;
  staffOnly: boolean;
  /** One per channel, in the same order as the columns they sit in. */
  parentSwitches: readonly (DraftParentSwitch & { source: NotificationPreferenceSource })[];
  rows: readonly AlertRowData[];
};

export type AlertSectionAccordionProps = {
  section: AlertSectionData;
  channelParentSwitches: readonly (DraftParentSwitch & { source: NotificationPreferenceSource })[];
  draft: NotificationPreferenceDraft;
  scope: NotificationPreferenceScope;
  expanded: boolean;
  onToggleExpanded: () => void;
  /** True while a write is in flight; see {@link PreferenceSwitch}. */
  disabled: boolean;
  onToggle: (kind: string, channel: string, enabled: boolean) => void;
  onEmailChoice: (kind: string, choice: EmailCadenceChoice) => void;
  setSectionChannel: (section: DeliverySection, channel: string, enabled: boolean) => void;
};

/**
 * The row of column headings.
 *
 * The channel columns are generated from {@link PREFERENCE_ROW_CHANNELS} rather
 * than written out, so a heading can never end up over the column it does not name.
 *
 * It carries no "muted from above" cue of its own. One badge per column restated
 * what the greyscale and each switch's own description already say, and at three
 * sections deep the page was mostly badges.
 */
function ColumnHeaders() {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: PREFERENCE_GRID_COLUMNS,
        alignItems: "flex-end",
        columnGap: 1,
        // Tight both ways: close under the section header it belongs to, and close
        // to the rule beneath, so the labels read as part of the first line rather
        // than as their own band.
        pt: 0.5,
        pb: 0.25,
        borderBottom: 1,
        borderColor: "divider",
      }}
    >
      <Typography variant="overline" color="text.secondary">
        Notification
      </Typography>

      {PREFERENCE_ROW_CHANNELS.map((channel) => (
        <Box key={channel} sx={CHANNEL_CELL_SX}>
          <Typography variant="overline" color="text.secondary">
            {channelLabel(channel)}
          </Typography>
        </Box>
      ))}

      {/* Caret lane, empty here. */}
      <Box />
    </Box>
  );
}

type SectionHeaderRowProps = {
  section: AlertSectionData;
  summaryId: string;
  detailsId: string;
  deliveringCount: number;
  expanded: boolean;
  onToggleExpanded: () => void;
};

/**
 * The section's heading band: its name, the "Off" marker and the caret.
 *
 * It stays the Accordion's FIRST child. MUI treats that child as the header and
 * exposes the expanded state through context, which is what lets `AccordionSummary`
 * live inside this component rather than directly under the accordion.
 */
function SectionHeaderRow({
  section,
  summaryId,
  detailsId,
  deliveringCount,
  expanded,
  onToggleExpanded,
}: SectionHeaderRowProps) {
  return (
    <>
      {/*
        Drawn on the notification grid so the caret lands in the caret lane rather
        than over the Email column.

        The caret is a SIBLING of the summary, never a descendant: a button inside
        the summary's own `<button>` is invalid HTML and undefined ARIA.
      */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: PREFERENCE_GRID_COLUMNS,
          alignItems: "center",
          columnGap: 1,
          px: 3,
          // The gray band is the section divider. It reads as a heading without
          // the ruled box the outlined variant drew, which is what made the
          // sections hard to tell apart from the rows inside them.
          backgroundColor: (theme) => theme.palette.surfaceSubtle,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        {/*
          The summary owns the name column and draws no caret of its own, because its
          built-in `expandIcon` renders at that column's right edge rather than in
          the caret lane.
        */}
        <AccordionSummary
          id={summaryId}
          aria-controls={detailsId}
          sx={{ minWidth: 0, px: 0, backgroundColor: "transparent" }}
        >
          {/*
            One line height across both, rather than each font's own. `overline` sets
            1.6 and `body2` 1.43, so centring two different line boxes left the count
            sitting a pixel or two off the title's optical centre — visible as soon as
            you look down a column of section headers.
          */}
          <Stack direction="row" alignItems="center" spacing={2} sx={{ minWidth: 0 }}>
            <Typography
              variant="overline"
              color="text.primary"
              sx={{ fontSize: "0.8125rem", fontWeight: 700, lineHeight: 1.5 }}
            >
              {section.label}
            </Typography>
            {deliveringCount === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                {SECTION_OFF_LABEL}
              </Typography>
            )}
            {section.staffOnly && (
              <Chip
                label="Encamp only"
                size="small"
                variant="outlined"
                sx={{
                  color: encampBrandColors.pumpkin,
                  borderColor: alpha(encampBrandColors.pumpkin, 0.5),
                }}
              />
            )}
          </Stack>
        </AccordionSummary>

        {/* Reserved though nothing draws in them: the grid is shared with the rows. */}
        {PREFERENCE_ROW_CHANNELS.map((channel) => (
          <Box key={channel} sx={CHANNEL_CELL_SX} />
        ))}

        {/*
          The caret, in its own lane past the channel columns.
        */}
        <Box sx={CHANNEL_CELL_SX}>
          <IconButton
            size="small"
            aria-label={`${expanded ? "Collapse" : "Expand"} ${section.label}`}
            aria-expanded={expanded}
            aria-controls={detailsId}
            onClick={() => onToggleExpanded()}
          >
            <ExpandMore
              sx={{
                transform: expanded ? "rotate(180deg)" : "none",
                transition: (theme) => theme.transitions.create("transform"),
              }}
            />
          </IconButton>
        </Box>
      </Box>
    </>
  );
}

export function AlertSectionAccordion({
  section,
  channelParentSwitches,
  draft,
  scope,
  expanded,
  onToggleExpanded,
  disabled,
  onToggle,
  onEmailChoice,
  setSectionChannel,
}: AlertSectionAccordionProps) {
  const deliveringCount = countDeliveringRows(section, draft, channelParentSwitches);
  const summaryId = useId();
  const detailsId = useId();

  return (
    /*
      A band inside the Alerts widget rather than a widget of its own. Five
      floating papers said nothing about what they collectively were, which is why
      it was hard to tell which section a given row belonged to.
    */
    /*
      Space below an expanded section rather than above every one. Collapsed
      sections read as a list and want to sit close together; an open one needs its
      rows separated from the next header.
    */
    <Box sx={{ mb: expanded ? 3 : 0 }}>
      <Accordion
        /*
        No outline and no shadow: the Alerts widget is the container, so an
        outlined accordion inside it drew a second box around the same content.
        What separates one section from the next is the gray header band below,
        which is quieter and matches how the rest of the app groups rows.
      */
        disableGutters
        elevation={0}
        expanded={expanded}
        onChange={onToggleExpanded}
        sx={{ "&::before": { display: "none" }, backgroundColor: "transparent" }}
        /*
        MUI wires the collapsible region from the *first child's* props, which is
        the header row below rather than the summary button. Naming the region from
        the button instead keeps its accessible name to "<section>, N of M on"
        rather than folding in the parent switch that sits alongside.
      */
        slotProps={{ region: { id: detailsId, "aria-labelledby": summaryId } }}
      >
        <SectionHeaderRow
          section={section}
          summaryId={summaryId}
          detailsId={detailsId}
          deliveringCount={deliveringCount}
          expanded={expanded}
          onToggleExpanded={onToggleExpanded}
        />
        <AccordionDetails sx={{ px: 3, pt: 0.5, pb: 1 }}>
          {/*
          The grid holds `PREFERENCE_GRID_MIN_WIDTH` of columns at their floor —
          restated as a reference rather than a number, because the last hard-coded
          figure went stale the moment a column gained a minimum. A narrow window
          scrolls it rather than crushing the alert names into a column of single
          characters. The scroll lives here rather than on the page so the Delivery
          card and the section headers stay put while the grid moves.
        */}
          <Box sx={{ overflowX: "auto" }}>
            <Box sx={{ minWidth: PREFERENCE_GRID_MIN_WIDTH }}>
              <ColumnHeaders />
              <SectionRollupRow
                section={section}
                channelParentSwitches={channelParentSwitches}
                draft={draft}
                scope={scope}
                disabled={disabled}
                setSectionChannel={setSectionChannel}
              />
              {/*
              A mute above marks each row's toggle columns rather than the whole
              block: the titles and descriptions stay legible, because a reader
              deciding whether to lift it has to be able to read what is under it.
              `AlertRow` applies that per channel, and nothing wraps it in a second
              layer — two nested treatments would compound into unreadable.
            */}
              {section.rows.map((row) => (
                <AlertRow
                  key={row.kind}
                  row={row}
                  sectionParentSwitches={section.parentSwitches}
                  channelParentSwitches={channelParentSwitches}
                  draft={draft}
                  scope={scope}
                  disabled={disabled}
                  onToggle={onToggle}
                  onEmailChoice={onEmailChoice}
                />
              ))}
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
